import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { FaLock, FaUser, FaQrcode, FaCamera } from 'react-icons/fa';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { userService } from '../services/userService';
import { toast } from 'react-toastify';

// Only allow same-origin relative paths to prevent open-redirect attacks
const safeNextPath = (raw) => {
  if (!raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
};

const LoginPage = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  useCompanySettings(false, true);

  const nextPath = safeNextPath(new URLSearchParams(location.search).get('next'));

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const qrInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanBufferRef = useRef({ chars: '', lastKeyTime: 0 });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const contextResult = await login(formData);
      setIsLoading(false);

      if (contextResult && contextResult.success) {
        navigate(nextPath);
      } else {
        setError(contextResult?.error || "Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.error("Unexpected error during login process:", error);
      setIsLoading(false);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleQRLogin = useCallback(async (codeValue) => {
    setError("");
    setIsLoading(true);

    try {
      const result = await userService.qrLogin(codeValue);

      if (result.success) {
        localStorage.setItem('access_token', result.data.access_token);
        localStorage.setItem('refresh_token', result.data.refresh_token);
        localStorage.setItem('user_data', JSON.stringify({
          user_id: result.data.user_id,
          username: result.data.username,
          email: result.data.email,
          is_superuser: result.data.is_superuser,
          is_staff: result.data.is_staff,
          groups: result.data.groups,
          customer_profile: result.data.customer_profile
        }));

        toast.success(`Welcome back, ${result.data.username}!`);

        navigate(nextPath);
        window.location.reload();
      } else {
        setError(result.error || "QR login failed");
      }
    } catch (error) {
      console.error("Unexpected error during QR login:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
      setShowQRScanner(false);
      setQrInput('');
    }
  }, [navigate, nextPath]);

  useEffect(() => {
    if (showQRScanner && qrInputRef.current) {
      setTimeout(() => {
        qrInputRef.current.focus();
      }, 100);
    }
  }, [showQRScanner]);

  // Keyboard-wedge scanners type very fast bursts ending with Enter — detect
  // them anywhere on the page so scanning works without opening the QR modal
  useEffect(() => {
    if (showQRScanner) return;

    const SCAN_GAP_MS = 100;
    const MIN_SCAN_LENGTH = 8;

    const handleScannerKeys = (e) => {
      if (isLoading || typeof e.key !== 'string') return;
      const buf = scanBufferRef.current;
      const now = Date.now();

      if (e.key === 'Enter') {
        if (buf.chars.length >= MIN_SCAN_LENGTH && now - buf.lastKeyTime < SCAN_GAP_MS) {
          e.preventDefault();
          e.stopPropagation();
          const code = buf.chars.trim();
          buf.chars = '';
          setFormData(prev => ({
            username: prev.username.replace(code, ''),
            password: prev.password.replace(code, '')
          }));
          handleQRLogin(code);
        } else {
          buf.chars = '';
        }
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (now - buf.lastKeyTime > SCAN_GAP_MS) {
          buf.chars = '';
        }
        buf.chars += e.key;
        buf.lastKeyTime = now;
      }
    };

    window.addEventListener('keydown', handleScannerKeys, true);
    return () => window.removeEventListener('keydown', handleScannerKeys, true);
  }, [showQRScanner, isLoading, handleQRLogin]);

  useEffect(() => {
    if (qrInput.trim() && showQRScanner && !isLoading) {
      const timer = setTimeout(() => {
        handleQRLogin(qrInput.trim());
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [qrInput, showQRScanner, isLoading, handleQRLogin]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsScanning(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Could not access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          try {
            track.stop();
          } catch (error) {
            console.warn('Error stopping camera track:', error);
          }
        });
        streamRef.current = null;
      }
    } catch (error) {
      console.error('Error during camera cleanup:', error);
    }
    setIsScanning(false);
  };

  const handleQRInputSubmit = (e) => {
    e.preventDefault();
    if (qrInput.trim()) {
      // Pass the entire code value to the QR login handler
      // The userService will handle the codes API call
      handleQRLogin(qrInput.trim());
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center bg-gray-50 pt-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-4">

       {/* Error Alert */}
       {error && (
         <div className="rounded-md bg-red-50 border border-red-200 p-4">
           <div className="flex">
             <div className="flex-shrink-0">
               <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
               </svg>
             </div>
             <div className="ml-3">
               <p className="text-sm text-red-800">{error}</p>
             </div>
           </div>
         </div>
       )}

       {/* Login Form */}
       <form onSubmit={handleSubmit} className="mt-8 space-y-6 bg-white shadow-xl rounded-lg px-8 pt-6 pb-8">
         <div className="space-y-5">
           {/* Username Input */}
           <div>
             <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
               Username
             </label>
             <div className="relative">
               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                 <FaUser className="h-5 w-5 text-gray-400" />
               </div>
               <input
                 id="username"
                 type="text"
                 name="username"
                 autoComplete="username"
                 value={formData.username}
                 onChange={handleChange}
                 required
                 disabled={isLoading}
                 className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                 placeholder="Enter your username"
               />
             </div>
           </div>

           {/* Password Input */}
           <div>
             <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
               Password
             </label>
             <div className="relative">
               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                 <FaLock className="h-5 w-5 text-gray-400" />
               </div>
               <input
                 id="password"
                 type="password"
                 name="password"
                 autoComplete="current-password"
                 value={formData.password}
                 onChange={handleChange}
                 required
                 disabled={isLoading}
                 className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                 placeholder="Enter your password"
               />
             </div>
           </div>
         </div>

         {/* Remember Me */}
         <div className="flex items-center">
           <input
             id="remember-me"
             type="checkbox"
             checked={rememberMe}
             onChange={(e) => setRememberMe(e.target.checked)}
             className="h-4 w-4 text-primary focus:ring-primary border-gray-200 rounded"
           />
           <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
             Remember me
           </label>
         </div>

         {/* Submit Button */}
         <button
           type="submit"
           disabled={isLoading}
           className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
         >
           {isLoading ? (
             <>
               <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
               </svg>
               Logging in...
             </>
           ) : (
             "Login"
           )}
         </button>
       </form>

       {/* QR Code Login Section */}
       <div className="mt-6 text-center">
         <div className="relative">
           <div className="absolute inset-0 flex items-center">
             <div className="w-full border-t border-gray-300" />
           </div>
           <div className="relative flex justify-center text-sm">
             <span className="px-2 bg-gray-50 text-gray-500">Or</span>
           </div>
         </div>
         
         <button
           type="button"
           onClick={() => setShowQRScanner(true)}
           className="mt-4 w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
         >
           <FaQrcode className="mr-2 h-5 w-5" />
           Scan QR Code
         </button>
       </div>

       {/* QR Scanner Modal */}
       {showQRScanner && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
           <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-medium text-gray-900">
                 QR Code Login
               </h3>
               <button
                 onClick={() => {
                   stopCamera();
                   setShowQRScanner(false);
                   setQrInput('');
                   setError('');
                 }}
                 className="text-gray-400 hover:text-gray-600"
               >
                 <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
             </div>

             <form onSubmit={handleQRInputSubmit} className="space-y-4">
               {/* Camera Scanner - Mobile Only */}
               <div className="block md:hidden">
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   <FaCamera className="inline h-4 w-4 mr-1" />
                   Camera Scanner
                 </label>
                 <div className="space-y-3">
                   <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                     {isScanning ? (
                       <div className="relative w-full h-full">
                         <video
                           ref={videoRef}
                           autoPlay
                           playsInline
                           className="w-full h-full object-cover"
                         />
                         <div className="absolute inset-4 border-2 border-primary border-dashed rounded-lg pointer-events-none">
                           <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-primary"></div>
                           <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-primary"></div>
                           <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-primary"></div>
                           <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-primary"></div>
                         </div>
                       </div>
                     ) : (
                       <div className="flex items-center justify-center h-full">
                         <div className="text-center">
                           <FaQrcode className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                           <p className="text-xs text-gray-600">Camera preview</p>
                         </div>
                       </div>
                     )}
                   </div>

                   <div className="flex space-x-2">
                     {!isScanning ? (
                       <button
                         type="button"
                         onClick={startCamera}
                         className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                       >
                         <FaCamera className="h-4 w-4 mr-2" />
                         Start Camera
                       </button>
                     ) : (
                       <button
                         type="button"
                         onClick={stopCamera}
                         className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                       >
                         Stop Camera
                       </button>
                     )}
                   </div>
                 </div>

                 <div className="relative my-4">
                   <div className="absolute inset-0 flex items-center">
                     <div className="w-full border-t border-gray-300" />
                   </div>
                   <div className="relative flex justify-center text-xs">
                     <span className="px-2 bg-white text-gray-500">Or scan with barcode scanner</span>
                   </div>
                 </div>
               </div>

               <div>
                 <label htmlFor="qr-input" className="block text-sm font-medium text-gray-700 mb-2">
                   Scan QR Code or Enter Code Manually
                 </label>
                 <textarea
                   id="qr-input"
                   ref={qrInputRef}
                   value={qrInput}
                   onChange={(e) => setQrInput(e.target.value)}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                       e.preventDefault();
                       if (qrInput.trim() && !isLoading) {
                         handleQRLogin(qrInput.trim());
                       }
                     }
                   }}
                   placeholder="Scan QR code here or paste the code manually..."
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary h-24 resize-none"
                   disabled={isLoading}
                   autoFocus
                 />
               </div>
               
               <div className="flex space-x-2">
                 <button
                   type="submit"
                   disabled={!qrInput.trim() || isLoading}
                   className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                 >
                   {isLoading ? (
                     <>
                       <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                       </svg>
                       Authenticating...
                     </>
                   ) : (
                     <>
                       <FaQrcode className="mr-2 h-4 w-4" />
                       Login with QR
                     </>
                   )}
                 </button>
                 
                 <button
                   type="button"
                   onClick={() => {
                     stopCamera();
                     setShowQRScanner(false);
                     setQrInput('');
                     setError('');
                   }}
                   className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                 >
                   Cancel
                 </button>
               </div>
               
               <div className="text-xs text-gray-500 text-center">
                 <p>Scan the QR code generated from user management or paste the code manually.</p>
                 <p className="mt-1">Touch screen users: Focus on the text area and scan the QR code.</p>
               </div>
             </form>
           </div>
         </div>
       )}
       
     </div>
   </div>
 );
};

export default LoginPage;